import { RoleKey, RoleNameMap } from '../account/hooks/useRoles';

// Define role hierarchy (higher index = higher privilege)
export const roleHierarchy = [
  RoleNameMap[RoleKey.RESTRICTED],
  RoleNameMap[RoleKey.GENERAL],
  RoleNameMap[RoleKey.WORKSPACE_ADMIN],
  RoleNameMap[RoleKey.GLOBAL_ADMIN],
];

/**
 * Check if a user can modify another user's role
 * @param currentUserRole - The role of the current user
 * @param targetUserRole - The role of the target user
 * @returns true if the current user can modify the target user's role
 */
export const canModifyUserRole = (currentUserRole: string, targetUserRole: string): boolean => {
  const currentUserIndex = roleHierarchy.indexOf(currentUserRole);
  const targetUserIndex = roleHierarchy.indexOf(targetUserRole);
  
  // Users can modify roles of users at the same level or below
  return currentUserIndex >= targetUserIndex;
};

/**
 * Check if a user can assign a specific role
 * @param currentUserRole - The role of the current user
 * @param targetRole - The role to be assigned
 * @returns true if the current user can assign the target role
 */
export const canAssignRole = (currentUserRole: string, targetRole: string): boolean => {
  const currentUserIndex = roleHierarchy.indexOf(currentUserRole);
  const targetRoleIndex = roleHierarchy.indexOf(targetRole);
  
  // Users can only assign roles at or below their own level
  return currentUserIndex >= targetRoleIndex;
};

/**
 * Get available roles for a user based on their permissions and Global Admin visibility
 * @param roleList - List of all available roles
 * @param currentUserRole - The role of the current user
 * @param showGlobalAdminRole - Whether to show Global Admin role
 * @returns Filtered list of roles the user can assign
 */
export const getAvailableRoles = (
  roleList: Array<{ id: string; name: string }>,
  currentUserRole?: string,
  showGlobalAdminRole: boolean = false
): Array<{ id: string; name: string }> => {
  let filteredRoles = roleList;

  // Filter based on current user's role permissions
  if (currentUserRole) {
    filteredRoles = filteredRoles.filter(role => canAssignRole(currentUserRole, role.name));
  }

  // Filter out Global Admin role if not allowed
  if (!showGlobalAdminRole) {
    filteredRoles = filteredRoles.filter(role => role.name !== 'Global Admin');
  }

  return filteredRoles;
}; 